import { loadSa, saveSa, subscribeSa } from '../api/profile.js';

/**
 * Connected-Swarasense (W1): the ONE shared Sa (shruti) control — a pitch-class
 * + octave `<select>` pair bound to `profile.sa`. Used by the raga-scale widget
 * and the tanpura widget (and conceptually matches the app's own Sa picker), so
 * every Sa control on the site reads and writes the same shruti. Choosing a Sa
 * on a raga page is your Sa in the tanpura and the practice tool too.
 *
 * Deliberately dependency-free beyond `profile.ts` (plain localStorage) so it
 * ships in the small `content-widgets` bundle on the static pages.
 */

/** 12-TET pitch classes, sharps-only — matches the app's Sa picker
 * (`settings-popover.ts` `PITCH_CLASSES`) and the seed spelling. */
export const SA_PITCH_CLASSES = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
] as const;

/** Octave choices — matches the app's Sa picker (`OCTAVES`) and the tanpura
 * page's prior `TANPURA_OCTAVES`. */
export const SA_OCTAVES = [2, 3, 4, 5] as const;

export interface SaControlHandle {
  /** The control's current Sa. */
  getSa(): { pitchClass: string; octave: number };
  /** Set the control's displayed Sa (does NOT persist — used to reflect an
   * external change; persistence happens on user edits + via `saveSa`). */
  setSa(sa: { pitchClass: string; octave: number }): void;
  /** Detach listeners (the cross-tab subscription). */
  destroy(): void;
}

const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );

/**
 * Render the Sa control into `container` (replacing its contents). Seeds from
 * `loadSa()`, persists to `profile.sa` on change (via `saveSa`), fires
 * `opts.onChange` with the new Sa, and reflects Sa changes made in other
 * tabs/pages (via `subscribeSa`). The dropdowns are always usable on their own.
 */
export function mountSaControl(
  container: HTMLElement,
  opts: { onChange?: (sa: { pitchClass: string; octave: number }) => void; label?: string } = {},
): SaControlHandle {
  let current = loadSa();
  const label = opts.label ?? 'Sa (shruti)';

  container.innerHTML = `
    <div class="sa-control" role="group" aria-label="${esc(label)}">
      <span class="sa-control-label">${esc(label)}</span>
      <select class="sa-control-pitch" aria-label="Sa pitch class">
        ${SA_PITCH_CLASSES.map(
          (p) => `<option value="${p}"${p === current.pitchClass ? ' selected' : ''}>${p}</option>`,
        ).join('')}
      </select>
      <select class="sa-control-octave" aria-label="Sa octave">
        ${SA_OCTAVES.map(
          (o) => `<option value="${o}"${o === current.octave ? ' selected' : ''}>${o}</option>`,
        ).join('')}
      </select>
    </div>`;

  const pitchSel = container.querySelector<HTMLSelectElement>('.sa-control-pitch')!;
  const octaveSel = container.querySelector<HTMLSelectElement>('.sa-control-octave')!;

  const applyToSelects = (sa: { pitchClass: string; octave: number }): void => {
    pitchSel.value = sa.pitchClass;
    octaveSel.value = String(sa.octave);
  };

  const onUserChange = (): void => {
    current = { pitchClass: pitchSel.value, octave: Number(octaveSel.value) };
    saveSa(current.pitchClass, current.octave); // persist the shared shruti
    opts.onChange?.(current);
  };
  pitchSel.addEventListener('change', onUserChange);
  octaveSel.addEventListener('change', onUserChange);

  // Reflect a Sa change made elsewhere (another tab / the app / another page).
  const unsubscribe = subscribeSa((sa) => {
    current = sa;
    applyToSelects(sa);
    opts.onChange?.(sa);
  });

  return {
    getSa: () => current,
    setSa: (sa) => {
      current = sa;
      applyToSelects(sa);
    },
    destroy: () => {
      unsubscribe();
    },
  };
}
